import { useState } from 'react';
import { Cloud, CloudOff, RefreshCw, Check, Copy } from 'lucide-react';
import type { PrefsSync } from '../lib/prefs-sync';

/**
 * 頂部「雲端同步」狀態徽章 + 同步碼面板。
 * 第一次按「啟用同步」會產生同步碼；把同步碼輸入其他裝置即可三台共用資料。
 */
export function CloudSyncBadge({ sync }: { sync: PrefsSync }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { syncId, status } = sync;
  const tone = status === 'error' ? 'var(--down)' : status === 'synced' ? 'var(--up)' : 'var(--text-muted)';
  const label = !syncId ? '未同步' : status === 'syncing' ? '同步中' : status === 'error' ? '同步失敗' : '已同步';
  const Icon = !syncId ? CloudOff : status === 'syncing' ? RefreshCw : status === 'error' ? CloudOff : Cloud;

  function copy() {
    if (!syncId) return;
    try {
      navigator.clipboard.writeText(syncId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {/**/}
  }

  function submitCode() {
    setErr(null);
    const ok = sync.applySyncId(input);
    if (!ok) setErr('同步碼格式不正確（8–64 碼英數字 / - / _）');
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded border border-(--border) px-2 py-1 text-[11px] text-(--text-secondary) hover:border-(--accent)"
        title="多裝置雲端同步"
      >
        <Icon size={12} className={status === 'syncing' ? 'animate-spin' : ''} style={{ color: tone }} />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-1 w-72 rounded border border-(--border) bg-(--bg-panel) p-3 shadow-lg">
          <div className="mb-2 text-[12px] font-bold text-(--text-primary)">☁️ 多裝置同步</div>

          {!syncId ? (
            <>
              <p className="mb-2 text-[11px] leading-relaxed text-(--text-secondary)">
                啟用後會產生一組同步碼，把它輸入到其他裝置（手機 / 另一台電腦），
                三台就會共用自選股、投資組合與購買力設定。
              </p>
              <button
                type="button"
                onClick={() => { sync.enableSync(); }}
                className="w-full rounded border border-(--accent) bg-(--accent)/15 px-2 py-1.5 text-[12px] font-semibold text-(--accent) hover:bg-(--accent)/25"
              >
                啟用同步
              </button>
            </>
          ) : (
            <>
              <div className="mb-1 text-[10px] text-(--text-muted)">這台裝置的同步碼</div>
              <div className="mb-2 flex items-center gap-1.5">
                <code className="min-w-0 flex-1 truncate rounded border border-(--border) bg-(--bg-base) px-2 py-1 text-[11px] text-(--text-primary)">
                  {syncId}
                </code>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1 rounded border border-(--border) px-1.5 py-1 text-[11px] text-(--text-secondary) hover:border-(--accent)"
                  title="複製同步碼"
                >
                  {copied ? <Check size={12} className="text-(--up)" /> : <Copy size={12} />}
                </button>
              </div>
              <p className="text-[10px] leading-relaxed text-(--text-muted)">
                在其他裝置點開這個面板，於下方欄位貼上同步碼即可加入同步。
              </p>
            </>
          )}

          <div className="mt-3 border-t border-(--border) pt-2">
            <div className="mb-1 text-[10px] text-(--text-muted)">在這台裝置輸入既有同步碼</div>
            <div className="flex items-center gap-1.5">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="貼上同步碼"
                className="min-w-0 flex-1 rounded border border-(--border) bg-(--bg-base) px-2 py-1 text-[11px] text-(--text-primary) outline-none focus:border-(--accent)"
              />
              <button
                type="button"
                onClick={submitCode}
                disabled={!input.trim()}
                className="rounded border border-(--accent) px-2 py-1 text-[11px] font-semibold text-(--accent) hover:bg-(--accent)/15 disabled:opacity-50"
              >
                套用
              </button>
            </div>
            {err && <div className="mt-1 text-[10px] text-(--down)">{err}</div>}
            <div className="mt-1 text-[10px] text-(--warn)">套用後會重新載入頁面，以雲端資料為準。</div>
          </div>
        </div>
      )}
    </div>
  );
}
